import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import * as errorHelpers from 'src/app/helpers/error.helpers';
import { StripeApi } from '../../api/stripe.api';
import { Order } from '../../models/order.model';
import { PlanTypes } from '../../models/plan.model';
import { PaymentMethodsApiActions } from '../payment-methods/payment-methods.actions';
import { selectDefaultPaymentMethod } from '../payment-methods/payment-methods.selectors';
import { SubscriptionsApiActions } from '../subscriptions/subscriptions.actions';
import {
  selectActiveSubscription,
  selectActiveSubscriptionId,
  selectDoesActiveSubscriptionHaveFamilyOption,
  selectFamilyOptionId,
  selectIsFamilyOptionAutoRenewCancelled,
} from '../subscriptions/subscriptions.selectors';
import { OrderActions, OrderApiActions } from './order.actions';
import {
  selectDoesOrderHaveFamilyOption,
  selectIsOrderAMigration,
  selectOrder,
  selectOrderPlan,
} from './order.selectors';
import { AlertMessage } from 'app/models/alert.model';
import { SubscriptionAlertsActions } from '../subscription-alerts/subscription-alerts.actions';
import { Coupon } from '../../models/coupon.model';
import { selectFamilyOption } from 'app/store/products/products.selectors';
import { ToasterService } from 'app/services/toaster.service';
import { KinoSubscription } from '../../models/kino-subscription.model';

@Injectable()
export class OrderEffects {
  private api: StripeApi = inject(StripeApi);
  private actions$: Actions = inject(Actions);
  private store: Store = inject(Store);
  private toasterService: ToasterService = inject(ToasterService);
  private router: Router = inject(Router);
  private translateService: TranslateService = inject(TranslateService);

  loadPaymentMethodsWhenInitOrderComponent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.initOrderComponent),
      map(() => {
        return PaymentMethodsApiActions.fetchPaymentMethods();
      })
    );
  });

  createOrderWhenInitOrderComponent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.initOrderComponent),
      switchMap((action) => {
        return this.store.select(selectActiveSubscription).pipe(
          map((activeSubscription: KinoSubscription) => {
            const { planType, voucherDuration } = action;
            const isMigration: boolean =
              activeSubscription?.plan?.name === PlanTypes.Month &&
              planType === PlanTypes.Year;
            const isMigrationWithFormerFamilyOption: boolean =
              isMigration &&
              !!activeSubscription.familyOption &&
              activeSubscription.familyOption.isAutoRenewed;
            const isOrderingFamilyOption: boolean =
              action.isOnlyFamily && !activeSubscription?.familyOption;

            const hasFamilyOption =
              isMigrationWithFormerFamilyOption || isOrderingFamilyOption;

            const coupon: Coupon | null = null;

            const order: Order = {
              planType,
              voucherDuration,
              hasFamilyOption,
              coupon,
            };

            return OrderActions.createOrder({ order });
          })
        );
      })
    );
  });

  startLoadingPaymentWhenProcessWithNoPaymentId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.startPaymentProcessWithNoPaymentId),
      map(() => {
        return OrderActions.startLoading();
      })
    );
  });

  startPayingWhenProcessWithNoPaymentId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.startPaymentProcessWithNoPaymentId),
      map(() => {
        return OrderApiActions.payOrderWithNoPaymentId();
      })
    );
  });

  payOrderWithNoPaymentId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.payOrderWithNoPaymentId),
      concatLatestFrom(() => [
        this.store.select(selectDefaultPaymentMethod),
        this.store.select(selectOrderPlan),
        this.store.select(selectIsOrderAMigration),
      ]),
      map(([_, defaultPaymentMethod, orderPlan, isMigration]) => {
        if (!defaultPaymentMethod) {
          this.router.navigate(['dashboard', 'setting', 'payment-methods']);
          return OrderActions.stopLoading();
        }
        if (isMigration) {
          return OrderApiActions.payMigration({
            defaultPaymentMethodId: defaultPaymentMethod.id,
          });
        }
        if (!!orderPlan) {
          return OrderApiActions.payOrderWithPaymentId({
            paymentId: defaultPaymentMethod.id,
          });
        }
        return OrderActions.stopLoading();
      })
    );
  });

  handlePayMigration$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.payMigration),
      concatLatestFrom(() => [this.store.select(selectOrderPlan)]),
      switchMap(([action, orderPlan]) => {
        return this.api.migratePlan$({ targetPlanId: orderPlan.id }).pipe(
          map(() => {
            return OrderApiActions.migrationPaid({
              defaultPaymentMethodId: action.defaultPaymentMethodId,
            });
          }),
          catchError((err) => {
            return of(
              OrderApiActions.failed({
                errorMessage: errorHelpers.getErrorMessage(
                  err,
                  this.translateService
                ),
              })
            );
          })
        );
      })
    );
  });

  decideWhatToDoWhenMigrationPaid$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.migrationPaid),
      concatLatestFrom(() => [
        this.store.select(selectDoesOrderHaveFamilyOption),
        this.store.select(selectDoesActiveSubscriptionHaveFamilyOption),
        this.store.select(selectIsFamilyOptionAutoRenewCancelled),
      ]),
      map(
        ([
          action,
          doesOrderHaveFamilyOption,
          doesActiveSubscriptionHaveFamilyOption,
          isFamilyOptionAutoRenewCancelled,
        ]) => {
          const isAddingFamilyOption: boolean =
            !doesActiveSubscriptionHaveFamilyOption &&
            doesOrderHaveFamilyOption;
          const isRemovingingFamilyOption: boolean =
            doesActiveSubscriptionHaveFamilyOption &&
            !isFamilyOptionAutoRenewCancelled &&
            !doesOrderHaveFamilyOption;
          const isReactivatingFamilyOption: boolean =
            isFamilyOptionAutoRenewCancelled && doesOrderHaveFamilyOption;

          if (isAddingFamilyOption) {
            return OrderApiActions.payFamilyOption({
              paymentId: action.defaultPaymentMethodId,
            });
          }

          if (isRemovingingFamilyOption) {
            return OrderApiActions.cancelFamilyOption({
              isMigration: true,
            });
          }

          if (isReactivatingFamilyOption) {
            return OrderApiActions.resumeFamilyOptionInMigrationProcess();
          }

          return OrderApiActions.paymentFinished();
        }
      )
    );
  });

  createTemporaryAlertWhenMigrationPaid = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.migrationPaid),
      map(() => {
        const isPermanent: boolean = false;
        const message: AlertMessage = {
          portions: [
            {
              text: 'dashboard.subscription.alerts.subscriptionBeingUpdated',
              action: null,
            },
          ],
        };
        return SubscriptionAlertsActions.createAlert({ message, isPermanent });
      })
    );
  });

  payOrderWithPaymentId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.payOrderWithPaymentId),
      concatLatestFrom(() => [this.store.select(selectOrder)]),
      map(([action, order]) => {
        const paymentId: string = action.paymentId;

        if (!!order.planType) {
          return OrderApiActions.paySubscription({ paymentId });
        }
        if (!order.voucherDuration && order.hasFamilyOption) {
          return OrderApiActions.payFamilyOption({ paymentId });
        }

        return OrderActions.stopLoading();
      })
    );
  });

  paySubscription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.paySubscription),
      concatLatestFrom(() => [
        this.store.select(selectDoesOrderHaveFamilyOption),
        this.store.select(selectOrderPlan),
      ]),
      map(([action, doesOrderHaveFamilyOption, orderPlan]) => {
        return {
          paymentId: action.paymentId,
          doesOrderHaveFamilyOption,
          orderPlan,
        };
      }),
      switchMap(({ paymentId, doesOrderHaveFamilyOption, orderPlan }) => {
        return this.api
          .subscribeToPlan$({
            productId: orderPlan.id,
            paymentId,
          })
          .pipe(
            map(() => {
              if (!doesOrderHaveFamilyOption) {
                return OrderApiActions.paymentFinished();
              }
              return OrderApiActions.payFamilyOption({ paymentId });
            }),
            catchError((err) => {
              return of(
                OrderApiActions.failed({
                  errorMessage: errorHelpers.getErrorMessage(
                    err,
                    this.translateService
                  ),
                })
              );
            })
          );
      })
    );
  });

  payFamilyOption$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.payFamilyOption),
      concatLatestFrom(() => this.store.select(selectFamilyOption)),
      switchMap(([action, familyOption]) => {
        return this.api
          .subscribeToPlan$({
            productId: familyOption.id,
            paymentId: action.paymentId,
          })
          .pipe(
            map(() => {
              return OrderApiActions.paymentFinished();
            }),
            catchError((err) => {
              return of(
                OrderApiActions.failed({
                  errorMessage: errorHelpers.getErrorMessage(
                    err,
                    this.translateService
                  ),
                })
              );
            })
          );
      })
    );
  });

  handlePaymentFinished$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.paymentFinished),
      map(() => {
        this.router.navigate([
          'dashboard',
          'subscriptions',
          'successful-payment',
        ]);
        return OrderActions.stopLoading();
      })
    );
  });

  handlePaymentError$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.failed),
      map((action) => {
        if (!!action.errorMessage) {
          this.toasterService.error(null, action.errorMessage);
        }
        return OrderActions.stopLoading();
      })
    );
  });

  startLoadingWhenCancelling$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.startCancelling),
      map(() => {
        return OrderActions.startLoading();
      })
    );
  });

  startCancelling$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.startCancelling),
      map((action) => {
        if (action.isOnlyFamilyOption) {
          return OrderApiActions.cancelFamilyOption({ isMigration: false });
        }
        return OrderApiActions.cancelActiveSubscription();
      })
    );
  });

  cancelActiveSubscription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.cancelActiveSubscription),
      concatLatestFrom(() => {
        return this.store.select(selectActiveSubscription);
      }),
      switchMap(([_, activeSubscription]) => {
        return this.api
          .cancelSubscription$({
            subscriptionId: activeSubscription?.id,
          })
          .pipe(
            map(() => {
              this.router.navigate(['dashboard', 'subscriptions']);
              return OrderApiActions.stopCancelling();
            }),
            catchError((err) => {
              return of(
                OrderApiActions.failed({
                  errorMessage: errorHelpers.getErrorMessage(
                    err,
                    this.translateService
                  ),
                })
              );
            })
          );
      })
    );
  });

  cancelFamilyOption$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.cancelFamilyOption),
      concatLatestFrom(() => [this.store.select(selectFamilyOptionId)]),
      switchMap(([action, familyOptionId]) => {
        return this.api
          .cancelSubscription$({ subscriptionId: familyOptionId })
          .pipe(
            map(() => {
              if (action.isMigration) {
                return OrderApiActions.paymentFinished();
              }
              this.router.navigate(['dashboard', 'subscriptions']);
              return OrderApiActions.stopCancelling();
            }),
            catchError((err) => {
              return of(
                OrderApiActions.failed({
                  errorMessage: errorHelpers.getErrorMessage(
                    err,
                    this.translateService
                  ),
                })
              );
            })
          );
      })
    );
  });

  stopLoadingAfterCancelling$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.stopCancelling),
      map(() => {
        return OrderActions.stopLoading();
      })
    );
  });

  fetchActiveSubscriptionAfterCancelling$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.stopCancelling),
      map(() => {
        return SubscriptionsApiActions.fetchActiveSubscription();
      })
    );
  });

  resume$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.startResuming),
      concatLatestFrom(() => [
        this.store.select(selectActiveSubscriptionId),
        this.store.select(selectFamilyOptionId),
      ]),
      switchMap(([action, activeSubscriptionId, familyOptionId]) => {
        // this.store.dispatch(CartActions.setFamilyOption({ isFamily: true }));
        const subscriptionId: number = action.isFamilyOption
          ? familyOptionId
          : activeSubscriptionId;
        return this.api.resumeSubscription$({ subscriptionId }).pipe(
          map(() => {
            return OrderApiActions.finishResuming({
              isFamilyOption: action.isFamilyOption,
            });
          }),
          catchError((err) => {
            return of(
              OrderApiActions.failed({
                errorMessage: errorHelpers.getErrorMessage(
                  err,
                  this.translateService
                ),
              })
            );
          })
        );
      })
    );
  });

  fetchActiveSubscriptionAfterResuming$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.finishResuming),
      map(() => {
        return SubscriptionsApiActions.fetchActiveSubscription();
      })
    );
  });

  createAlertAfterResuming$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.finishResuming),
      map((action) => {
        let text: string;
        if (action.isFamilyOption) {
          text = 'dashboard.subscription.alerts.effectiveFamilyOptionRenewal';
        } else {
          text = 'dashboard.subscription.alerts.effectiveSubscriptionRenewal';
        }
        return SubscriptionAlertsActions.createAlert({
          isPermanent: false,
          message: {
            portions: [
              {
                text,
                action: null,
              },
            ],
          },
        });
      })
    );
  });

  navigateToHomeAfterResuming$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderApiActions.finishResuming),
        tap(() => {
          this.router.navigate(['dashboard', 'subscriptions']);
        })
      );
    },
    {
      dispatch: false,
    }
  );

  resumeFamilyOptionInMigrationProcess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderApiActions.resumeFamilyOptionInMigrationProcess),
      concatLatestFrom(() => this.store.select(selectFamilyOptionId)),
      switchMap(([_, familyOptionId]) => {
        return this.api
          .resumeSubscription$({ subscriptionId: familyOptionId })
          .pipe(
            map(() => {
              return OrderApiActions.paymentFinished();
            }),
            catchError((err) => {
              return of(
                OrderApiActions.failed({
                  errorMessage: errorHelpers.getErrorMessage(
                    err,
                    this.translateService
                  ),
                })
              );
            })
          );
      })
    );
  });
}
